import { useRef, useState } from 'react';
import moment from 'moment';
import { makeStyles, TextField, Button, Paper } from '@material-ui/core';
import { PrintRounded as PrintIcon } from '@material-ui/icons';
import { useReactToPrint } from 'react-to-print';

import DeliveryNotesList from '../components/Lists/DeliveryNotesList.component';
import { useGetOrdersDeliveryNotes } from '../hooks/get-orders-delivery-notes.hook';
import { useDownloadDeliveryNotesCsv } from '../hooks/download-delivery-notes-csv.hook';
import { PrintDeliveryNotesContainer } from '../components/Print/PrintDeliveryNotesContainer.component';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '0.7rem',

    '& > h2': {
      margin: 0,
    },
  },

  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
  },

  actionsFilters: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },

  actionsBtns: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },

  primaryButton: {
    textTransform: 'none',
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },

  outlinedButton: {
    textTransform: 'none',
    backgroundColor: 'transparent',
    color: theme.palette.admin.secondary.main,
    borderColor: theme.palette.admin.secondary.main,
    '&:hover': {
      color: theme.palette.admin.secondary.contrastText,
      borderColor: theme.palette.admin.secondary.light,
      backgroundColor: theme.palette.admin.secondary.light,
    },
  },

  deliveryNotes: {
    padding: '1rem',
  },
}));

type ActionContainerProps = {
  deliveryDate: string;
  printDisabled: boolean;
  downloadDisabled: boolean;
  onDeliveryDateChange: (deliveryDate: string) => void;
  onPrint: () => void;
  onDownload: () => void;
};

function ActionContainer({
  deliveryDate,
  printDisabled,
  downloadDisabled,
  onDeliveryDateChange,
  onPrint,
  onDownload,
}: ActionContainerProps) {
  const classes = useStyles();

  return (
    <section className={classes.actions}>
      <div className={classes.actionsFilters}>
        <TextField
          size="small"
          label="Date de livraison"
          type="date"
          variant="outlined"
          value={deliveryDate}
          onChange={(event) => onDeliveryDateChange(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </div>

      <div className={classes.actionsBtns}>
        <Button
          disabled={downloadDisabled}
          onClick={onDownload}
          className={classes.outlinedButton}
          size="small"
          variant="outlined"
          color="primary"
        >
          Télécharger
        </Button>
        <Button
          disabled={printDisabled}
          onClick={onPrint}
          className={classes.primaryButton}
          startIcon={<PrintIcon />}
          size="small"
          variant="contained"
          color="primary"
        >
          Imprimer
        </Button>
      </div>
    </section>
  );
}

export default function AdminOrdersDeliveryNotesList() {
  const classes = useStyles();

  const [deliveryDate, setDeliveryDate] = useState(moment().format('YYYY-MM-DD'));
  const { loading, deliveryNotes } = useGetOrdersDeliveryNotes(deliveryDate);
  const { downloading, downloadCsv } = useDownloadDeliveryNotesCsv();

  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ content: () => printRef.current });

  const printDisabled = loading || !deliveryNotes || deliveryNotes.length === 0;
  const downloadDisabled = loading || !deliveryNotes || deliveryNotes.length === 0 || downloading;

  return (
    <section className={classes.root}>
      <h2>Bons de livraison</h2>
      <ActionContainer
        deliveryDate={deliveryDate}
        printDisabled={printDisabled}
        downloadDisabled={downloadDisabled}
        onDeliveryDateChange={setDeliveryDate}
        onPrint={handlePrint}
        onDownload={() => downloadCsv(deliveryDate)}
      />
      <Paper className={classes.deliveryNotes}>
        <DeliveryNotesList deliveryNotes={deliveryNotes} loading={loading} />
      </Paper>
      <PrintDeliveryNotesContainer ref={printRef} deliveryNotes={loading ? null : deliveryNotes} />
    </section>
  );
}
